import { UserRole } from "@features/settings/types/settings.types";
import { ConfirmationModal } from "@shared/components/ConfirmationModal";
import ModalBlank from "@shared/components/modals/ModalBlank";
import SearchForm from "@shared/components/SearchForm";
import { UserProfileDisplay } from "@shared/components/UserProfileDisplay";
import { ArrowLeftSmallIcon } from "@shared/components/ui/Icon";
import { useProfile } from "@shared/hooks/useProfile";
import AuthenticatedLayout from "@shared/layouts/AuthenticatedLayout";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import ResourceForm from "../components/ResourceForm";
import { ResourceMaterialItem } from "../components/ResourceMaterialItem";
import { useResourcesStore } from "../store/useResourcesStore";
import { ResourceMaterial } from "../types/resource.types";

const ResourceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    currentResource,
    isLoadingCurrent,
    currentError,
    fetchResource,
    updateResource,
    deleteResource,
  } = useResourcesStore();

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Delete confirmation modal state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filter and search state
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  // User profile for permissions
  const { user, isLoading: loadingProfile } = useProfile();

  // Check if user can edit/delete the resource
  const canEdit =
    user &&
    currentResource &&
    (currentResource.authorId === user.id || user.role === UserRole.ADMIN);

  // Fetch resource on mount or when id changes
  useEffect(() => {
    if (id) {
      fetchResource(id);
    }
  }, [id, fetchResource]);

  // Handle resource update
  const handleUpdateResource = async (data: any) => {
    if (!id) return;

    setIsSubmitting(true);
    try {
      const updated = await updateResource(id, data);
      if (updated) {
        setShowEditModal(false);
        toast.success("Resource updated successfully");
      } else {
        toast.error("Failed to update resource");
      }
    } catch (error) {
      console.error("Error updating resource:", error);
      toast.error("An error occurred while updating the resource");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show delete confirmation modal
  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  // Handle resource delete
  const handleDeleteResource = async () => {
    if (!id) return;

    setIsDeleting(true);
    try {
      const success = await deleteResource(id);
      if (success) {
        toast.success("Resource deleted successfully");
        navigate("/resources");
      } else {
        toast.error("Failed to delete resource");
        setShowDeleteConfirmation(false);
      }
    } catch (error) {
      console.error("Error deleting resource:", error);
      toast.error("An error occurred while deleting the resource");
      setShowDeleteConfirmation(false);
    } finally {
      setIsDeleting(false);
    }
  };

  // Filter materials by category and search query
  const filteredMaterials = currentResource?.materials?.filter((material) => {
    const matchesCategory =
      !selectedCategory || material.category === selectedCategory;
    const matchesSearch =
      !searchQuery ||
      material.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      material.category?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Get unique categories from materials
  const uniqueCategories =
    currentResource?.materials?.reduce((acc: string[], material) => {
      if (material.category && !acc.includes(material.category)) {
        acc.push(material.category);
      }
      return acc;
    }, []) || [];

  return (
    <AuthenticatedLayout
      isLoading={isLoadingCurrent || loadingProfile}
      error={currentError ? new Error(currentError) : null}
      pageHeader={{
        title: "",
        actions: [],
        search: {
          show: false,
        },
      }}
    >
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        {currentResource && (
          <>
            {/* Back button */}
            <div className="mb-6">
              <button
                className="btn-sm px-3 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300"
                onClick={() => navigate("/resources")}
              >
                <ArrowLeftSmallIcon className="fill-current text-gray-400 dark:text-gray-500 mr-2" />
                <span>Back To Resources</span>
              </button>
            </div>

            {/* Main content */}
            <div className="mx-auto flex flex-col lg:flex-row lg:space-x-8 xl:space-x-16">
              {/* Left column - Content */}
              <div className="flex-1">
                {/* Resource header */}
                <div className="mb-6">
                  <div className="flex justify-between items-center">
                    <h1 className="text-3xl text-gray-900 dark:text-white mb-2">
                      {currentResource.title}
                    </h1>
                    {canEdit && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setShowEditModal(true)}
                          className="flex items-center px-3 py-2 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors duration-150"
                        >
                          <svg
                            className="w-4 h-4 mr-2 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                          <span>Edit</span>
                        </button>
                        
                        <button
                          onClick={handleDeleteClick}
                          className="flex items-center px-3 py-2 rounded-md bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors duration-150"
                        >
                          <svg
                            className="w-4 h-4 mr-2 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <div
                    className="text-base text-gray-700 dark:text-gray-200 mb-4"
                    dangerouslySetInnerHTML={{
                      __html: currentResource.description || "",
                    }}
                  />
                </div>

                {/* Created by */}
                <div className="mb-4">
                  <UserProfileDisplay 
                    profile={currentResource.author} 
                    label="Created by"
                    showAvatar={true}
                    avatarSize="sm"
                    className="w-full"
                  />
                </div>
              </div>

              {/* Right column - Featured image and actions */}
              <div className="flex-1">
                {/* Featured image */}
                {currentResource.featuredImage &&
                  currentResource.featuredImage.length > 0 &&
                  currentResource.featuredImage[0].variants &&
                  currentResource.featuredImage[0].variants.length > 0 && (
                    <div className="mb-6 bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
                      <img
                        src={currentResource.featuredImage[0].variants[0].url}
                        alt={currentResource.title}
                        className="w-full h-auto object-cover"
                      />
                    </div>
                  )}
              </div>
            </div>

            {/* Resources section */}
            <div className="mt-8 border-t pt-6">
              <div className="flex flex-row mb-6 justify-between">
                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-0">
                  Resources
                </h2>

                {/* Search */}
                <div className="w-64">
                  <SearchForm
                    placeholder="Search resources..."
                    onChange={(value) => setSearchQuery(value)}
                  />
                </div>
              </div>

              {/* Category badges with counters */}
              <div className="flex flex-wrap gap-2 mb-6">
                <button
                  onClick={() => setSelectedCategory("")}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center ${
                    selectedCategory === ""
                      ? "bg-black text-white"
                      : "bg-white text-gray-800 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }`}
                >
                  View All
                  <span
                    className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                      selectedCategory === ""
                        ? "bg-gray-100 bg-opacity-20 text-white"
                        : "bg-gray-100 bg-opacity-70"
                    }`}
                  >
                    {currentResource?.materials?.length || 0}
                  </span>
                </button>
                {uniqueCategories.map((category) => {
                  // Count materials in this category
                  const count =
                    currentResource?.materials?.filter(
                      (m) => m.category === category
                    ).length || 0;
                  return (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center ${
                        category === selectedCategory
                          ? "bg-black text-white"
                          : "bg-white text-gray-800 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                      }`}
                    >
                      {category}
                      <span
                        className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                          category === selectedCategory
                            ? "bg-gray-100 bg-opacity-20 text-white"
                            : "bg-gray-100 bg-opacity-70"
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Materials grid */}
              {filteredMaterials && filteredMaterials.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  {filteredMaterials.map(
                    (material: ResourceMaterial, index: number) => (
                      <div
                        key={index}
                        className="group relative rounded-lg overflow-hidden transition-shadow duration-300"
                      >
                        {/* Material content */}
                        <ResourceMaterialItem material={material} />
                      </div>
                    )
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                  {searchQuery || selectedCategory
                    ? "No materials found matching your filters."
                    : "No materials available for this resource."}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Edit Modal */}
      {currentResource && (
        <ModalBlank
          id="resource-edit-modal"
          className="max-h-[80vh] overflow-y-auto p-4"
          modalOpen={showEditModal}
          setModalOpen={setShowEditModal}
          showCloseButton={true}
        >
          <ResourceForm
            resource={currentResource}
            onSubmit={handleUpdateResource}
            onCancel={() => setShowEditModal(false)}
            isSubmitting={isSubmitting}
            canEdit={!!canEdit}
            canDelete={!!canEdit}
            onDelete={handleDeleteResource}
          />
        </ModalBlank>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteResource}
        isLoading={isDeleting}
        title="Delete Resource"
        message="Are you sure you want to delete this resource? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </AuthenticatedLayout>
  );
};

export default ResourceDetailPage;
